// web/src/scoring/landmarks.ts
// FRAME Landmark Detection — On-device GPS-based landmark lookup
// Patent ref: Provisional #2 — Landmark spike coefficients
// No photo data transmitted. GPS coords from EXIF only.

import { LandmarkTier } from '@frame/scoring-types';

interface LandmarkEntry {
  name: string;
  tier: LandmarkTier;
  lat: number;
  lng: number;
  radiusKm: number;
}

// ─── Iconic Landmarks (tier 3, coefficient 1.5×) ───────────────
const ICONIC: LandmarkEntry[] = [
  // Europe
  { name: 'Eiffel Tower', tier: LandmarkTier.Iconic, lat: 48.8584, lng: 2.2945, radiusKm: 0.3 },
  { name: 'Colosseum', tier: LandmarkTier.Iconic, lat: 41.8902, lng: 12.4922, radiusKm: 0.3 },
  { name: 'Sagrada Familia', tier: LandmarkTier.Iconic, lat: 41.4036, lng: 2.1744, radiusKm: 0.2 },
  { name: 'Big Ben', tier: LandmarkTier.Iconic, lat: 51.5007, lng: -0.1246, radiusKm: 0.3 },
  { name: 'Tower Bridge', tier: LandmarkTier.Iconic, lat: 51.5055, lng: -0.0754, radiusKm: 0.3 },
  { name: 'Leaning Tower of Pisa', tier: LandmarkTier.Iconic, lat: 43.7230, lng: 10.3966, radiusKm: 0.2 },
  { name: 'Acropolis', tier: LandmarkTier.Iconic, lat: 37.9715, lng: 23.7267, radiusKm: 0.3 },
  { name: 'Santorini Caldera', tier: LandmarkTier.Iconic, lat: 36.4166, lng: 25.4315, radiusKm: 1.5 },
  { name: 'Neuschwanstein Castle', tier: LandmarkTier.Iconic, lat: 47.5576, lng: 10.7498, radiusKm: 0.5 },
  { name: 'Louvre Museum', tier: LandmarkTier.Iconic, lat: 48.8606, lng: 2.3376, radiusKm: 0.3 },
  { name: 'Notre-Dame', tier: LandmarkTier.Iconic, lat: 48.8530, lng: 2.3499, radiusKm: 0.2 },
  { name: 'Stonehenge', tier: LandmarkTier.Iconic, lat: 51.1789, lng: -1.8262, radiusKm: 0.5 },
  { name: 'Kremlin', tier: LandmarkTier.Iconic, lat: 55.7520, lng: 37.6175, radiusKm: 0.4 },
  { name: 'Hagia Sophia', tier: LandmarkTier.Iconic, lat: 41.0086, lng: 28.9802, radiusKm: 0.2 },
  { name: 'Blue Mosque', tier: LandmarkTier.Iconic, lat: 41.0054, lng: 28.9768, radiusKm: 0.2 },
  { name: 'Alhambra', tier: LandmarkTier.Iconic, lat: 37.1761, lng: -3.5881, radiusKm: 0.4 },
  { name: 'Charles Bridge', tier: LandmarkTier.Iconic, lat: 50.0865, lng: 14.4114, radiusKm: 0.3 },
  { name: 'Trevi Fountain', tier: LandmarkTier.Iconic, lat: 41.9009, lng: 12.4833, radiusKm: 0.1 },
  // Americas
  { name: 'Statue of Liberty', tier: LandmarkTier.Iconic, lat: 40.6892, lng: -74.0445, radiusKm: 0.5 },
  { name: 'Golden Gate Bridge', tier: LandmarkTier.Iconic, lat: 37.8199, lng: -122.4783, radiusKm: 0.5 },
  { name: 'Times Square', tier: LandmarkTier.Iconic, lat: 40.7580, lng: -73.9855, radiusKm: 0.2 },
  { name: 'Grand Canyon', tier: LandmarkTier.Iconic, lat: 36.1069, lng: -112.1129, radiusKm: 5.0 },
  { name: 'Niagara Falls', tier: LandmarkTier.Iconic, lat: 43.0799, lng: -79.0747, radiusKm: 0.5 },
  { name: 'Machu Picchu', tier: LandmarkTier.Iconic, lat: -13.1631, lng: -72.5450, radiusKm: 0.5 },
  { name: 'Christ the Redeemer', tier: LandmarkTier.Iconic, lat: -22.9519, lng: -43.2105, radiusKm: 0.3 },
  { name: 'Chichen Itza', tier: LandmarkTier.Iconic, lat: 20.6843, lng: -88.5678, radiusKm: 0.5 },
  { name: 'Hollywood Sign', tier: LandmarkTier.Iconic, lat: 34.1341, lng: -118.3215, radiusKm: 0.5 },
  { name: 'Brooklyn Bridge', tier: LandmarkTier.Iconic, lat: 40.7061, lng: -73.9969, radiusKm: 0.3 },
  { name: 'Central Park', tier: LandmarkTier.Iconic, lat: 40.7829, lng: -73.9654, radiusKm: 1.5 },
  // Asia
  { name: 'Taj Mahal', tier: LandmarkTier.Iconic, lat: 27.1751, lng: 78.0421, radiusKm: 0.3 },
  { name: 'Great Wall of China', tier: LandmarkTier.Iconic, lat: 40.4319, lng: 116.5704, radiusKm: 2.0 },
  { name: 'Angkor Wat', tier: LandmarkTier.Iconic, lat: 13.4125, lng: 103.8670, radiusKm: 1.0 },
  { name: 'Mount Fuji', tier: LandmarkTier.Iconic, lat: 35.3606, lng: 138.7274, radiusKm: 5.0 },
  { name: 'Forbidden City', tier: LandmarkTier.Iconic, lat: 39.9163, lng: 116.3972, radiusKm: 0.5 },
  { name: 'Petronas Towers', tier: LandmarkTier.Iconic, lat: 3.1578, lng: 101.7117, radiusKm: 0.3 },
  { name: 'Marina Bay Sands', tier: LandmarkTier.Iconic, lat: 1.2834, lng: 103.8607, radiusKm: 0.3 },
  { name: 'Burj Khalifa', tier: LandmarkTier.Iconic, lat: 25.1972, lng: 55.2744, radiusKm: 0.3 },
  // Africa & Middle East
  { name: 'Pyramids of Giza', tier: LandmarkTier.Iconic, lat: 29.9792, lng: 31.1342, radiusKm: 1.0 },
  { name: 'Sphinx', tier: LandmarkTier.Iconic, lat: 29.9753, lng: 31.1376, radiusKm: 0.3 },
  { name: 'Petra', tier: LandmarkTier.Iconic, lat: 30.3285, lng: 35.4444, radiusKm: 1.0 },
  { name: 'Table Mountain', tier: LandmarkTier.Iconic, lat: -33.9628, lng: 18.4098, radiusKm: 2.0 },
  // Oceania
  { name: 'Sydney Opera House', tier: LandmarkTier.Iconic, lat: -33.8568, lng: 151.2153, radiusKm: 0.3 },
  { name: 'Sydney Harbour Bridge', tier: LandmarkTier.Iconic, lat: -33.8523, lng: 151.2108, radiusKm: 0.3 },
  { name: 'Uluru', tier: LandmarkTier.Iconic, lat: -25.3444, lng: 131.0369, radiusKm: 2.0 },
];

// ─── Notable Landmarks (tier 2, coefficient 1.25×) ─────────────
const NOTABLE: LandmarkEntry[] = [
  // Europe
  { name: 'Arc de Triomphe', tier: LandmarkTier.Notable, lat: 48.8738, lng: 2.2950, radiusKm: 0.2 },
  { name: 'Buckingham Palace', tier: LandmarkTier.Notable, lat: 51.5014, lng: -0.1419, radiusKm: 0.3 },
  { name: 'Vatican City', tier: LandmarkTier.Notable, lat: 41.9029, lng: 12.4534, radiusKm: 0.4 },
  { name: 'Pantheon', tier: LandmarkTier.Notable, lat: 41.8986, lng: 12.4769, radiusKm: 0.1 },
  { name: 'Versailles', tier: LandmarkTier.Notable, lat: 48.8049, lng: 2.1204, radiusKm: 0.8 },
  { name: 'Brandenburg Gate', tier: LandmarkTier.Notable, lat: 52.5163, lng: 13.3777, radiusKm: 0.2 },
  { name: 'Reichstag', tier: LandmarkTier.Notable, lat: 52.5186, lng: 13.3761, radiusKm: 0.2 },
  { name: 'Edinburgh Castle', tier: LandmarkTier.Notable, lat: 55.9486, lng: -3.1999, radiusKm: 0.3 },
  { name: 'Rialto Bridge', tier: LandmarkTier.Notable, lat: 45.4381, lng: 12.3360, radiusKm: 0.1 },
  { name: 'St Mark\'s Square', tier: LandmarkTier.Notable, lat: 45.4341, lng: 12.3388, radiusKm: 0.2 },
  { name: 'Duomo di Milano', tier: LandmarkTier.Notable, lat: 45.4642, lng: 9.1920, radiusKm: 0.2 },
  { name: 'Prague Castle', tier: LandmarkTier.Notable, lat: 50.0911, lng: 14.4014, radiusKm: 0.3 },
  { name: 'Matterhorn', tier: LandmarkTier.Notable, lat: 45.9763, lng: 7.6586, radiusKm: 2.0 },
  { name: 'Mont Blanc', tier: LandmarkTier.Notable, lat: 45.8326, lng: 6.8652, radiusKm: 3.0 },
  { name: 'Amsterdam Canals', tier: LandmarkTier.Notable, lat: 52.3702, lng: 4.8952, radiusKm: 0.8 },
  { name: 'Amalfi Coast', tier: LandmarkTier.Notable, lat: 40.6340, lng: 14.6027, radiusKm: 3.0 },
  { name: 'Dubrovnik Old Town', tier: LandmarkTier.Notable, lat: 42.6507, lng: 18.0944, radiusKm: 0.5 },
  { name: 'Cinque Terre', tier: LandmarkTier.Notable, lat: 44.1062, lng: 9.7265, radiusKm: 3.0 },
  // Americas
  { name: 'Empire State Building', tier: LandmarkTier.Notable, lat: 40.7484, lng: -73.9857, radiusKm: 0.2 },
  { name: 'Lincoln Memorial', tier: LandmarkTier.Notable, lat: 38.8893, lng: -77.0502, radiusKm: 0.2 },
  { name: 'Washington Monument', tier: LandmarkTier.Notable, lat: 38.8895, lng: -77.0353, radiusKm: 0.2 },
  { name: 'Alcatraz Island', tier: LandmarkTier.Notable, lat: 37.8267, lng: -122.4230, radiusKm: 0.3 },
  { name: 'Yellowstone Old Faithful', tier: LandmarkTier.Notable, lat: 44.4605, lng: -110.8281, radiusKm: 1.0 },
  { name: 'Monument Valley', tier: LandmarkTier.Notable, lat: 36.9984, lng: -110.0985, radiusKm: 5.0 },
  { name: 'Iguazu Falls', tier: LandmarkTier.Notable, lat: -25.6953, lng: -54.4367, radiusKm: 1.0 },
  { name: 'Antelope Canyon', tier: LandmarkTier.Notable, lat: 36.8619, lng: -111.3743, radiusKm: 0.5 },
  { name: 'Havana Malecon', tier: LandmarkTier.Notable, lat: 23.1450, lng: -82.3590, radiusKm: 1.0 },
  { name: 'Space Needle', tier: LandmarkTier.Notable, lat: 47.6205, lng: -122.3493, radiusKm: 0.2 },
  { name: 'Waikiki Beach', tier: LandmarkTier.Notable, lat: 21.2793, lng: -157.8292, radiusKm: 0.5 },
  // Asia
  { name: 'Fushimi Inari Shrine', tier: LandmarkTier.Notable, lat: 34.9671, lng: 135.7727, radiusKm: 0.3 },
  { name: 'Sensoji Temple', tier: LandmarkTier.Notable, lat: 35.7148, lng: 139.7967, radiusKm: 0.2 },
  { name: 'Tokyo Tower', tier: LandmarkTier.Notable, lat: 35.6586, lng: 139.7454, radiusKm: 0.2 },
  { name: 'Shibuya Crossing', tier: LandmarkTier.Notable, lat: 35.6595, lng: 139.7004, radiusKm: 0.1 },
  { name: 'Ha Long Bay', tier: LandmarkTier.Notable, lat: 20.9101, lng: 107.1839, radiusKm: 5.0 },
  { name: 'Bali Rice Terraces', tier: LandmarkTier.Notable, lat: -8.4315, lng: 115.2793, radiusKm: 2.0 },
  { name: 'Jaipur Hawa Mahal', tier: LandmarkTier.Notable, lat: 26.9239, lng: 75.8267, radiusKm: 0.2 },
  { name: 'Golden Temple Amritsar', tier: LandmarkTier.Notable, lat: 31.6200, lng: 74.8765, radiusKm: 0.3 },
  { name: 'Gardens by the Bay', tier: LandmarkTier.Notable, lat: 1.2816, lng: 103.8636, radiusKm: 0.4 },
  { name: 'Bagan Temples', tier: LandmarkTier.Notable, lat: 21.1717, lng: 94.8585, radiusKm: 5.0 },
  // Africa
  { name: 'Victoria Falls', tier: LandmarkTier.Notable, lat: -17.9243, lng: 25.8567, radiusKm: 1.0 },
  { name: 'Serengeti', tier: LandmarkTier.Notable, lat: -2.3333, lng: 34.8333, radiusKm: 20.0 },
  { name: 'Marrakech Medina', tier: LandmarkTier.Notable, lat: 31.6295, lng: -7.9811, radiusKm: 1.0 },
  // Oceania
  { name: 'Great Barrier Reef', tier: LandmarkTier.Notable, lat: -18.2871, lng: 147.6992, radiusKm: 30.0 },
  { name: 'Milford Sound', tier: LandmarkTier.Notable, lat: -44.6724, lng: 167.9267, radiusKm: 3.0 },
];

// ─── Interesting Landmarks (tier 1, coefficient 1.1×) ──────────
const INTERESTING: LandmarkEntry[] = [
  // National Parks & Nature
  { name: 'Yosemite Valley', tier: LandmarkTier.Interesting, lat: 37.7456, lng: -119.5936, radiusKm: 5.0 },
  { name: 'Zion National Park', tier: LandmarkTier.Interesting, lat: 37.2982, lng: -113.0263, radiusKm: 5.0 },
  { name: 'Glacier National Park', tier: LandmarkTier.Interesting, lat: 48.7596, lng: -113.7870, radiusKm: 10.0 },
  { name: 'Banff National Park', tier: LandmarkTier.Interesting, lat: 51.4968, lng: -115.9281, radiusKm: 10.0 },
  { name: 'Torres del Paine', tier: LandmarkTier.Interesting, lat: -51.0000, lng: -73.0000, radiusKm: 10.0 },
  { name: 'Dolomites', tier: LandmarkTier.Interesting, lat: 46.4102, lng: 11.8440, radiusKm: 10.0 },
  { name: 'Swiss Alps Jungfrau', tier: LandmarkTier.Interesting, lat: 46.5369, lng: 7.9623, radiusKm: 5.0 },
  { name: 'Lake Bled', tier: LandmarkTier.Interesting, lat: 46.3625, lng: 14.0986, radiusKm: 1.0 },
  { name: 'Plitvice Lakes', tier: LandmarkTier.Interesting, lat: 44.8654, lng: 15.5820, radiusKm: 3.0 },
  { name: 'Blue Lagoon Iceland', tier: LandmarkTier.Interesting, lat: 63.8791, lng: -22.4507, radiusKm: 0.5 },
  { name: 'Northern Lights Tromso', tier: LandmarkTier.Interesting, lat: 69.6492, lng: 18.9553, radiusKm: 10.0 },
  { name: 'Galapagos Islands', tier: LandmarkTier.Interesting, lat: -0.9538, lng: -90.9656, radiusKm: 20.0 },
  { name: 'Death Valley', tier: LandmarkTier.Interesting, lat: 36.5323, lng: -116.9325, radiusKm: 10.0 },
  { name: 'Bryce Canyon', tier: LandmarkTier.Interesting, lat: 37.5930, lng: -112.1871, radiusKm: 5.0 },
  { name: 'Arches National Park', tier: LandmarkTier.Interesting, lat: 38.7331, lng: -109.5925, radiusKm: 5.0 },
  { name: 'Joshua Tree', tier: LandmarkTier.Interesting, lat: 33.8734, lng: -115.9010, radiusKm: 10.0 },
  { name: 'Sequoia National Park', tier: LandmarkTier.Interesting, lat: 36.4864, lng: -118.5658, radiusKm: 10.0 },
  { name: 'Everglades', tier: LandmarkTier.Interesting, lat: 25.2866, lng: -80.8987, radiusKm: 15.0 },
  { name: 'Acadia National Park', tier: LandmarkTier.Interesting, lat: 44.3386, lng: -68.2733, radiusKm: 5.0 },
  { name: 'Mount Rainier', tier: LandmarkTier.Interesting, lat: 46.8523, lng: -121.7603, radiusKm: 5.0 },
  // Cities & Urban Landmarks
  { name: 'Venice Grand Canal', tier: LandmarkTier.Interesting, lat: 45.4408, lng: 12.3155, radiusKm: 0.5 },
  { name: 'Barcelona Las Ramblas', tier: LandmarkTier.Interesting, lat: 41.3818, lng: 2.1700, radiusKm: 0.3 },
  { name: 'Vienna State Opera', tier: LandmarkTier.Interesting, lat: 48.2034, lng: 16.3688, radiusKm: 0.2 },
  { name: 'Zurich Old Town', tier: LandmarkTier.Interesting, lat: 47.3769, lng: 8.5417, radiusKm: 0.5 },
  { name: 'Lisbon Belem Tower', tier: LandmarkTier.Interesting, lat: 38.6916, lng: -9.2160, radiusKm: 0.3 },
  { name: 'Copenhagen Nyhavn', tier: LandmarkTier.Interesting, lat: 55.6800, lng: 12.5900, radiusKm: 0.2 },
  { name: 'Bergen Bryggen', tier: LandmarkTier.Interesting, lat: 60.3975, lng: 5.3225, radiusKm: 0.3 },
  { name: 'Bruges Old Town', tier: LandmarkTier.Interesting, lat: 51.2093, lng: 3.2247, radiusKm: 0.5 },
  { name: 'Kyoto Bamboo Grove', tier: LandmarkTier.Interesting, lat: 35.0170, lng: 135.6713, radiusKm: 0.3 },
  { name: 'Seoul Gyeongbokgung', tier: LandmarkTier.Interesting, lat: 37.5796, lng: 126.9770, radiusKm: 0.3 },
  { name: 'Bangkok Grand Palace', tier: LandmarkTier.Interesting, lat: 13.7500, lng: 100.4913, radiusKm: 0.3 },
  { name: 'Dubai Palm Jumeirah', tier: LandmarkTier.Interesting, lat: 25.1124, lng: 55.1390, radiusKm: 2.0 },
  { name: 'Cape Town Waterfront', tier: LandmarkTier.Interesting, lat: -33.9036, lng: 18.4208, radiusKm: 0.5 },
  // Beaches
  { name: 'Maldives', tier: LandmarkTier.Interesting, lat: 3.2028, lng: 73.2207, radiusKm: 30.0 },
  { name: 'Bora Bora', tier: LandmarkTier.Interesting, lat: -16.5004, lng: -151.7415, radiusKm: 5.0 },
  { name: 'Maya Bay', tier: LandmarkTier.Interesting, lat: 7.6788, lng: 98.7649, radiusKm: 0.5 },
  { name: 'Copacabana Beach', tier: LandmarkTier.Interesting, lat: -22.9711, lng: -43.1822, radiusKm: 0.5 },
  { name: 'Cancun Beach', tier: LandmarkTier.Interesting, lat: 21.1619, lng: -86.8515, radiusKm: 2.0 },
  { name: 'Phi Phi Islands', tier: LandmarkTier.Interesting, lat: 7.7407, lng: 98.7784, radiusKm: 2.0 },
];

const LANDMARK_DB: LandmarkEntry[] = [...ICONIC, ...NOTABLE, ...INTERESTING];

/**
 * Detect the nearest landmark from GPS coordinates using Haversine distance.
 * Returns the highest-tier landmark within its detection radius.
 * Runs entirely on-device — no network calls.
 */
export function detectLandmarkFromGPS(
  lat: number,
  lng: number,
): { tier: LandmarkTier; name: string } {
  const R = 6371; // Earth radius in km
  let bestTier = LandmarkTier.Ordinary;
  let bestName = '';
  let bestDist = Infinity;

  for (const lm of LANDMARK_DB) {
    const dLat = (lm.lat - lat) * Math.PI / 180;
    const dLng = (lm.lng - lng) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat * Math.PI / 180) * Math.cos(lm.lat * Math.PI / 180) *
      Math.sin(dLng / 2) ** 2;
    const dist = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    if (dist <= lm.radiusKm) {
      // Prefer higher tier; break ties by closer distance
      if (lm.tier > bestTier || (lm.tier === bestTier && dist < bestDist)) {
        bestTier = lm.tier;
        bestName = lm.name;
        bestDist = dist;
      }
    }
  }

  return { tier: bestTier, name: bestName };
}
